// src/pages/inventory/commerce/shopify/tabs/ProductMergeTab.tsx
import React, { useState, useEffect } from "react";
import { Button, Space, Tag, Statistic, Row, Col, Typography, Alert, Modal, Radio, Checkbox, Divider, Descriptions } from "antd";
import { ContrastTag } from "../../../../components/shared";
import type { ColumnsType } from "antd/es/table";
import type { ProductMergeRequest } from "../../../../types/inventory/ProductTypes";
import { CheckCircleOutlined, CloseCircleOutlined, EyeOutlined, MergeCellsOutlined, WarningOutlined } from "@ant-design/icons";
import { fetchProductMergeRequests, fetchMergeRequestStatistics, approveProductMergeRequest, rejectProductMergeRequest } from "../../../../api/inventory/productMergeApi";
import { useNotifications } from "../../../../context/NotificationContext";
import { getSessionUser } from "../../../../utils/auth/sessionUser";
import { SectionCard, DataTable, StatusTag, InlineLoading, EmptyState } from "../../../../components/shared";

const { Title, Text, Paragraph } = Typography;

const ProductMergeTab: React.FC = () => {
	const [mergeRequests, setMergeRequests] = useState<ProductMergeRequest[]>([]);
	const [statistics, setStatistics] = useState<any>(null);
	const [loading, setLoading] = useState(true);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalRequests, setTotalRequests] = useState(0);
	const [selectedRequest, setSelectedRequest] = useState<ProductMergeRequest | null>(null);
	const [detailModalVisible, setDetailModalVisible] = useState(false);
	const [approveModalVisible, setApproveModalVisible] = useState(false);
	const [rejectModalVisible, setRejectModalVisible] = useState(false);
	const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
	const [mergeDirection, setMergeDirection] = useState<"shopify_overwrites" | "local_overwrites" | "selective">("shopify_overwrites");
	const [selectiveFields, setSelectiveFields] = useState<string[]>([]);
	const [rejectReason, setRejectReason] = useState("");
	const [processing, setProcessing] = useState(false);
	const { showError, showSuccess } = useNotifications();

	const perPage = 10;

	useEffect(() => {
		loadData();
	}, [currentPage]);

	const loadData = async () => {
		setLoading(true);
		try {
			const [requestsResult, statsResult] = await Promise.all([
				fetchProductMergeRequests({
					status: "pending",
					per_page: perPage,
					page: currentPage,
				}),
				fetchMergeRequestStatistics(),
			]);

			setMergeRequests(requestsResult.data.data || []);
			setTotalRequests(requestsResult.data.total || 0);
			setStatistics(statsResult.data);
		} catch (error) {
			console.error("Failed to load merge requests:", error);
			showError("Failed to load product merge requests");
		} finally {
			setLoading(false);
		}
	};

	const handleViewDetails = (request: ProductMergeRequest) => {
		setSelectedRequest(request);
		setDetailModalVisible(true);
	};

	const handleApproveClick = (request: ProductMergeRequest) => {
		setSelectedRequest(request);
		if (request.similar_products_details && request.similar_products_details.length > 0) {
			setSelectedProductId(request.similar_products_details[0].product.product_id);
		}
		setMergeDirection("shopify_overwrites");
		setSelectiveFields([]);
		setApproveModalVisible(true);
	};

	const handleRejectClick = (request: ProductMergeRequest) => {
		setSelectedRequest(request);
		setRejectReason("");
		setRejectModalVisible(true);
	};

	const handleApprove = async () => {
		if (!selectedRequest || !selectedProductId) {
			showError("Please select a product to merge with");
			return;
		}

		setProcessing(true);
		try {
			const user = getSessionUser();

			await approveProductMergeRequest(selectedRequest.id, {
				user_id: user.id,
				selected_product_id: selectedProductId,
				merge_direction: mergeDirection,
				selective_merge_fields: mergeDirection === "selective" ? selectiveFields : undefined,
			});

			showSuccess("Merge request approved successfully");
			setApproveModalVisible(false);
			loadData();
		} catch (error) {
			showError("Failed to approve merge request");
		} finally {
			setProcessing(false);
		}
	};

	const handleReject = async () => {
		if (!selectedRequest) return;

		setProcessing(true);
		try {
			const user = getSessionUser();

			await rejectProductMergeRequest(selectedRequest.id, {
				user_id: user.id,
				reason: rejectReason,
			});

			showSuccess("Merge request rejected successfully");
			setRejectModalVisible(false);
			loadData();
		} catch (error) {
			showError("Failed to reject merge request");
		} finally {
			setProcessing(false);
		}
	};

	const columns: ColumnsType<ProductMergeRequest> = [
		{
			title: "Shopify Product",
			key: "shopify_product",
			render: (_, record) => (
				<div>
					<Text strong>{record.shopify_product_data?.title || "Unknown"}</Text>
					<br />
					<Text type="secondary" style={{ fontSize: 12 }}>
						ID: {record.shopify_product_id}
					</Text>
				</div>
			),
		},
		{
			title: "Similar Products",
			key: "similar_count",
			width: 150,
			align: "center",
			render: (_, record) => <ContrastTag background="var(--mg-primary-bg)" borderColor="var(--mg-primary-border)">{Object.keys(record.similar_products || {}).length} matches</ContrastTag>,
		},
		{
			title: "Requested",
			dataIndex: "requested_at",
			key: "requested_at",
			width: 150,
			render: (date: string) => new Date(date).toLocaleDateString(),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: 120,
			render: (status: string) => {
				const colors: Record<string, string> = {
					pending: "orange",
					approved: "green",
					rejected: "red",
					processed: "blue",
				};
				return <Tag color={colors[status]}>{status.toUpperCase()}</Tag>;
			},
		},
		{
			title: "Actions",
			key: "actions",
			width: 200,
			render: (_, record) => (
				<Space size="small">
					<Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleViewDetails(record)}>
						View
					</Button>
					{record.status === "pending" && (
						<>
							<Button type="link" size="small" icon={<CheckCircleOutlined />} onClick={() => handleApproveClick(record)}>
								Approve
							</Button>
							<Button type="link" size="small" danger icon={<CloseCircleOutlined />} onClick={() => handleRejectClick(record)}>
								Reject
							</Button>
						</>
					)}
				</Space>
			),
		},
	];

	const selectiveFieldOptions = [
		{ label: "Name", value: "name" },
		{ label: "Description", value: "description" },
		{ label: "Price", value: "price" },
		{ label: "Cost", value: "cost" },
		{ label: "SKU", value: "sku" },
		{ label: "Barcode", value: "barcode" },
		{ label: "Images", value: "images" },
		{ label: "Variants", value: "variants" },
	];

	return (
		<div>
			<Space direction="vertical" style={{ width: "100%" }} size="large">
				<Alert
					message="Product Merge Requests"
					description="When importing products from Shopify, similar products in your local inventory are detected. Review and approve merges to keep your inventory synchronized."
					type="info"
					showIcon
					icon={<MergeCellsOutlined />}
				/>

				{statistics && (
					<Row gutter={16}>
						<Col xs={24} sm={12} md={6}>
							<SectionCard>
								<Statistic title="Pending" value={statistics.pending} valueStyle={{ color: "var(--mg-warning)" }} prefix={<WarningOutlined />} />
							</SectionCard>
						</Col>
						<Col xs={24} sm={12} md={6}>
							<SectionCard>
								<Statistic title="Approved" value={statistics.approved} valueStyle={{ color: "var(--mg-success)" }} prefix={<CheckCircleOutlined />} />
							</SectionCard>
						</Col>
						<Col xs={24} sm={12} md={6}>
							<SectionCard>
								<Statistic title="Rejected" value={statistics.rejected} valueStyle={{ color: "var(--mg-danger)" }} prefix={<CloseCircleOutlined />} />
							</SectionCard>
						</Col>
						<Col xs={24} sm={12} md={6}>
							<SectionCard>
								<Statistic title="Total" value={statistics.total} prefix={<MergeCellsOutlined />} />
							</SectionCard>
						</Col>
					</Row>
				)}

				{loading ? (
					<InlineLoading message="Loading merge requests…" minHeight={200} />
				) : (
					<DataTable
						columns={columns}
						dataSource={mergeRequests}
						rowKey="id"
						total={totalRequests}
						currentPage={currentPage}
						pageSize={perPage}
						onPageChange={setCurrentPage}
						emptyTitle="No pending merge requests"
						emptyDescription="All merge requests have been processed"
					/>
				)}
			</Space>

			{/* Detail Modal */}
			<Modal title="Merge Request Details" open={detailModalVisible} onCancel={() => setDetailModalVisible(false)} footer={null} width={800}>
				{selectedRequest && (
					<Space direction="vertical" style={{ width: "100%" }} size="large">
						<SectionCard title="Shopify Product">
							<Descriptions column={1} bordered size="small">
								<Descriptions.Item label="Title">{selectedRequest.shopify_product_data?.title}</Descriptions.Item>
								<Descriptions.Item label="Shopify ID">{selectedRequest.shopify_product_id}</Descriptions.Item>
								<Descriptions.Item label="Vendor">{selectedRequest.shopify_product_data?.vendor || "—"}</Descriptions.Item>
								<Descriptions.Item label="Product Type">{selectedRequest.shopify_product_data?.product_type || "—"}</Descriptions.Item>
							</Descriptions>
						</SectionCard>

						<SectionCard title="Similar Local Products">
							{selectedRequest.similar_products_details && selectedRequest.similar_products_details.length > 0 ? (
								<Space direction="vertical" style={{ width: "100%" }}>
									{selectedRequest.similar_products_details.map((item) => (
										<SectionCard key={item.product.product_id} >
											<Row gutter={16}>
												<Col span={18}>
													<Space direction="vertical" size="small">
														<Text strong>{item.product.name}</Text>
														<Space>
															<Text type="secondary">SKU: {item.product.sku}</Text>
															<Text type="secondary">Price: ${item.product.unit_price ? Number(item.product.unit_price).toFixed(2) : "0.00"}</Text>
														</Space>
													</Space>
												</Col>
												<Col span={6} style={{ textAlign: "right" }}>
													<Tag color="blue">Similarity: {(item.similarity_score * 100).toFixed(0)}%</Tag>
												</Col>
											</Row>
										</SectionCard>
									))}
								</Space>
							) : (
								<Text type="secondary">No similar products found</Text>
							)}
						</SectionCard>
					</Space>
				)}
			</Modal>

			{/* Approve Modal */}
			<Modal title="Approve Merge Request" open={approveModalVisible} onCancel={() => setApproveModalVisible(false)} onOk={handleApprove} okText="Approve" confirmLoading={processing} width={700}>
				{selectedRequest && (
					<Space direction="vertical" style={{ width: "100%" }} size="large">
						<Alert message="Select a product to merge with and choose how the data should be merged." type="info" showIcon />

						<div>
							<Title level={5}>Select Product to Merge</Title>
							<Radio.Group value={selectedProductId} onChange={(e) => setSelectedProductId(e.target.value)} style={{ width: "100%" }}>
								<Space direction="vertical" style={{ width: "100%" }}>
									{selectedRequest.similar_products_details && Array.isArray(selectedRequest.similar_products_details) ? (
										selectedRequest.similar_products_details.map((item) => (
											<Radio key={item.product.product_id} value={item.product.product_id} style={{ width: "100%" }}>
												<SectionCard style={{ width: "100%" }}>
													<Space direction="vertical" size="small" style={{ width: "100%" }}>
														<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
															<Text strong>{item.product.name}</Text>
															<Tag style={{ background: "var(--mg-primary-bg)", borderColor: "var(--mg-primary-border)", color: "var(--mg-primary)" }}>{(item.similarity_score * 100).toFixed(0)}% match</Tag>
														</div>
														<Space>
															<Text type="secondary">SKU: {item.product.sku}</Text>
															<Text type="secondary">Price: ${item.product.unit_price ? Number(item.product.unit_price).toFixed(2) : "0.00"}</Text>
														</Space>
													</Space>
												</SectionCard>
											</Radio>
										))
									) : (
										<EmptyState description="No similar products available for merging" />
									)}
								</Space>
							</Radio.Group>
						</div>

						<Divider />

						<div>
							<Title level={5}>Merge Direction</Title>
							<Radio.Group value={mergeDirection} onChange={(e) => setMergeDirection(e.target.value)}>
								<Space direction="vertical">
									<Radio value="shopify_overwrites">
										<Space direction="vertical" size={0}>
											<Text strong>Shopify Overwrites Local</Text>
											<Text type="secondary" style={{ fontSize: 12 }}>
												Use Shopify data for all fields
											</Text>
										</Space>
									</Radio>
									<Radio value="local_overwrites">
										<Space direction="vertical" size={0}>
											<Text strong>Local Overwrites Shopify</Text>
											<Text type="secondary" style={{ fontSize: 12 }}>
												Use local data for all fields
											</Text>
										</Space>
									</Radio>
									<Radio value="selective">
										<Space direction="vertical" size={0}>
											<Text strong>Selective Merge</Text>
											<Text type="secondary" style={{ fontSize: 12 }}>
												Choose which fields to take from Shopify
											</Text>
										</Space>
									</Radio>
								</Space>
							</Radio.Group>
						</div>

						{mergeDirection === "selective" && (
							<div>
								<Title level={5}>Select Fields from Shopify</Title>
								<Checkbox.Group options={selectiveFieldOptions} value={selectiveFields} onChange={(values) => setSelectiveFields(values as string[])} />
							</div>
						)}
					</Space>
				)}
			</Modal>

			{/* Reject Modal */}
			<Modal title="Reject Merge Request" open={rejectModalVisible} onCancel={() => setRejectModalVisible(false)} onOk={handleReject} okText="Reject" okType="danger" confirmLoading={processing}>
				<Space direction="vertical" style={{ width: "100%" }} size="large">
					<Alert message="Are you sure you want to reject this merge request?" type="warning" showIcon />
					<div>
						<Text>Reason (optional):</Text>
						<textarea
							value={rejectReason}
							onChange={(e) => setRejectReason(e.target.value)}
							rows={4}
							style={{
								width: "100%",
								marginTop: 8,
								padding: 8,
								border: "1px solid #d9d9d9",
								borderRadius: 4,
							}}
							placeholder="Enter reason for rejection..."
						/>
					</div>
				</Space>
			</Modal>
		</div>
	);
};

export default ProductMergeTab;
